import { useEffect } from "react";
import { useParams } from "react-router";

import { MasterDetailLayout } from "common/components";
import { getPageId } from "common/utilities";
import { useDispatch, useMemoisedSelector, useSelector } from "common/utilities/react-redux";
import { useTranslate } from "common/utilities/useTranslate";
import { getSiteLanguage } from "domains/language/selectors";
import { fetchMembershipEventById, fetchMembershipEvents } from "domains/membershipEvents/actions";
import { MembershipEventDetail } from "domains/membershipEvents/components/MembershipEventDetail";
import { MembershipEventList } from "domains/membershipEvents/components/MembershipEventList";
import {
  createMembershipEventsSelector,
  getMembershipEventsById,
  getMembershipEventsIds,
  getMembershipEventsIsPending,
} from "domains/membershipEvents/selectors";
import type { MembershipEvent } from "domains/membershipEvents/types";
import { getHasAccessToMembershipEvents } from "entities/permissions/selectors";

const getEventList = (list: MembershipEvent[], event: MembershipEvent) => {
  if (list.length === 0 && event) {
    return [event];
  }
  return list || [];
};

export const UpcomingEventsPage = () => {
  const dispatch = useDispatch();
  const items = useMemoisedSelector(getMembershipEventsIds, createMembershipEventsSelector);
  const { eventId } = useParams();
  const hasAccess = useSelector(getHasAccessToMembershipEvents);
  const isPending = useSelector(getMembershipEventsIsPending);
  const siteLanguage = useSelector(getSiteLanguage);
  const translate = useTranslate();

  const id = getPageId(eventId, items);

  const event = useSelector(getMembershipEventsById(id));

  useEffect(() => {
    if (items.length === 0 && hasAccess) {
      dispatch(fetchMembershipEvents());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, siteLanguage, hasAccess]);

  useEffect(() => {
    if (id) {
      dispatch(fetchMembershipEventById({ id }));
    }
  }, [id, dispatch]);

  if (!event) {
    return null;
  }

  const list = (
    <MembershipEventList
      items={getEventList(items, event)}
      isPending={isPending}
      selectedId={id}
      showSeparators={hasAccess}
    />
  );

  return (
    <MasterDetailLayout
      listTitle={translate("app.event.upcoming_events")}
      listComponent={list}
      mainView={eventId ? "right" : "left"}
      listBackNavigation={{
        label: translate("app.navigation.back"),
        path: "/",
      }}
      detailBackNavigation={{
        label: translate("app.event.upcoming_events"),
        path: "/membership-events",
      }}
    >
      <MembershipEventDetail event={event} />
    </MasterDetailLayout>
  );
};
